import React, { useEffect, useState, Component } from 'react';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { MetricChart } from '../components/MetricChart';
import { ListWidget } from '../components/ListWidget';
import { CompanySelector, Company } from '../components/CompanySelector';
import { DateRangePicker } from '../components/DateRangePicker';
import { GranularitySelector, Granularity, getAvailableGranularities, getDefaultGranularity } from '../components/GranularitySelector';
import { ComparisonSelector, ComparisonType } from '../components/ComparisonSelector';
import { PaymentMethodFilter, PaymentMethod } from '../components/PaymentMethodFilter';
import { AddChartModal } from '../components/AddChartModal';
import { EditChartsModal } from '../components/EditChartsModal';
import { AnalyticsSection } from '../components/AnalyticsWidgets';
import { InfoTooltip } from '../../../shared/ui/InfoTooltip';
import { BalanceSnapshotWidget } from '../components/BalanceSnapshotWidget';
import { AttentionNeededWidget } from '../components/AttentionNeededWidget';
import { RecommendationsWidget } from '../components/RecommendationsWidget';
import { CreditCardIcon, ArrowUpRightIcon, ArrowDownRightIcon, BarChart3Icon, DollarSignIcon, UsersIcon, ClockIcon, PercentIcon, CheckCircleIcon, GlobeIcon, FilterIcon, RotateCcwIcon, ReceiptIcon, RefreshCcwIcon, AlertTriangleIcon, WalletIcon, ChevronRightIcon, ActivityIcon, ServerIcon, LinkIcon, FileTextIcon, CalendarIcon, BellIcon } from 'lucide-react';
import { formatCurrencyAmount, getTenant } from '../../../theme/tokens';
import { useCompany } from '../../../app/providers/CompanyProvider';
import { generatePaymentVolumeData, generateTransactionCountData, generateAverageTransactionData, generatePaymentMethodsData, generateConversionData, generateAttemptsData, generateGeoPaymentData, generateCompanies, filterDataByCompanies, generateRefundData, generatePendingRefundsData, generateRefundReasonsData, generatePreviousPeriodData, generatePreviousTransactionCountData, generatePreviousRefundData, generateDisputeCountData, generatePreviousDisputeCountData, generateDisputeActivityData, generatePreviousDisputeActivityData, generateRefundRateData, generatePreviousRefundRateData, generateNewCustomersData, generatePreviousNewCustomersData, generateBlockedPaymentsData, generatePreviousBlockedPaymentsData, generateBlockedPaymentsVolumeData, generatePreviousBlockedPaymentsVolumeData, generatePreviousAverageTransactionData } from '../components/DataGenerator';
import { mockBills } from '../../bills/mocks/mockBills';
type TabType = 'dashboard' | 'actions';
export function Overview() {
  const {
    t
  } = useTranslation('analytics');
  const navigate = useNavigate();
  const {
    selectedCompany
  } = useCompany();
  const [activeTab, setActiveTab] = useState<TabType>('dashboard');
  // --- Analytics State ---
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<Company[]>([]);
  const [showAddChartModal, setShowAddChartModal] = useState(false);
  const [showEditChartModal, setShowEditChartModal] = useState(false);
  const [chartOrder, setChartOrder] = useState<string[]>(['gross-volume', 'net-volume', 'successful-payments', 'payment-success-rate', 'refunds-volume', 'refund-rate', 'total-payments', 'dispute-count', 'dispute-activity', 'disputed-payments', 'failed-payments', 'payment-methods', 'new-customers', 'spend-per-customer', 'top-customers', 'top-countries', 'blocked-payments', 'blocked-payments-volume']);
  const [visibleCharts, setVisibleCharts] = useState<Set<string>>(new Set(['gross-volume', 'net-volume', 'successful-payments', 'payment-success-rate', 'refunds-volume', 'refund-rate', 'total-payments', 'dispute-count', 'dispute-activity', 'disputed-payments', 'failed-payments', 'payment-methods', 'new-customers', 'spend-per-customer', 'top-customers', 'top-countries', 'blocked-payments', 'blocked-payments-volume']));
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setDate(new Date().getDate() - 7)),
    end: new Date()
  });
  const [granularity, setGranularity] = useState<Granularity>('daily');
  // Compute available granularities based on selected date range
  const availableGranularities = getAvailableGranularities(dateRange);
  const isGranularityHidden = availableGranularities.length <= 1;
  // When date range changes, auto-adjust granularity if current selection is invalid
  const handleDateRangeChange = (newRange: {
    start: Date;
    end: Date;
  }) => {
    setDateRange(newRange);
    const newAvailable = getAvailableGranularities(newRange);
    if (!newAvailable.includes(granularity)) {
      setGranularity(getDefaultGranularity(newRange));
    }
  };
  // Handle granularity change (no longer syncs date range — date range drives granularity options)
  const handleGranularityChange = (newGranularity: Granularity) => {
    setGranularity(newGranularity);
  };
  const [comparison, setComparison] = useState<ComparisonType>('previous_period');
  const [customComparisonRange, setCustomComparisonRange] = useState<{
    start: Date;
    end: Date;
  } | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  // Chart Data State
  const [paymentVolumeData, setPaymentVolumeData] = useState<any[]>([]);
  const [previousPaymentVolumeData, setPreviousPaymentVolumeData] = useState<any[]>([]);
  const [transactionCountData, setTransactionCountData] = useState<any[]>([]);
  const [previousTransactionCountData, setPreviousTransactionCountData] = useState<any[]>([]);
  const [averageTransactionData, setAverageTransactionData] = useState<any[]>([]);
  const [previousAverageTransactionData, setPreviousAverageTransactionData] = useState<any[]>([]);
  const [paymentMethodsData, setPaymentMethodsData] = useState<any[]>([]);
  const [geoPaymentData, setGeoPaymentData] = useState<any[]>([]);
  const [refundData, setRefundData] = useState<any[]>([]);
  const [disputeActivityData, setDisputeActivityData] = useState<any[]>([]);
  const [refundRateData, setRefundRateData] = useState<any[]>([]);
  const [previousRefundRateData, setPreviousRefundRateData] = useState<any[]>([]);
  const [newCustomersData, setNewCustomersData] = useState<any[]>([]);
  const [previousNewCustomersData, setPreviousNewCustomersData] = useState<any[]>([]);
  const [disputeCountData, setDisputeCountData] = useState<any[]>([]);
  const [previousDisputeCountData, setPreviousDisputeCountData] = useState<any[]>([]);
  const [blockedPaymentsData, setBlockedPaymentsData] = useState<any[]>([]);
  const [previousBlockedPaymentsData, setPreviousBlockedPaymentsData] = useState<any[]>([]);
  const [blockedPaymentsVolumeData, setBlockedPaymentsVolumeData] = useState<any[]>([]);
  const [previousBlockedPaymentsVolumeData, setPreviousBlockedPaymentsVolumeData] = useState<any[]>([]);
  // --- Load Companies & Portal ---
  useEffect(() => {
    const companiesData = generateCompanies();
    setCompanies(companiesData);
    setSelectedCompanies(companiesData.length > 0 ? [companiesData[0]] : []);
  }, []);
  useEffect(() => {
    const sidebarContainer = document.getElementById('sidebar-company-selector');
    if (sidebarContainer && companies.length > 0) {
      ReactDOM.render(<CompanySelector companies={companies} selectedCompanies={selectedCompanies} onChange={setSelectedCompanies} data-id="element-2460" />, sidebarContainer);
    }
    return () => {
      if (sidebarContainer) {
        ReactDOM.unmountComponentAtNode(sidebarContainer);
      }
    };
  }, [companies, selectedCompanies]);
  // --- Generate Data ---
  useEffect(() => {
    const startDate = dateRange.start instanceof Date ? dateRange.start : new Date(dateRange.start);
    const endDate = dateRange.end instanceof Date ? dateRange.end : new Date(dateRange.end);
    // Use selectedCompany.id as seed for sub-merchant-specific data
    const companySeed = selectedCompany?.id || '';
    const rawPaymentVolumeData = generatePaymentVolumeData(companySeed, startDate, endDate, granularity);
    const rawPreviousPaymentVolumeData = generatePreviousPeriodData(rawPaymentVolumeData, companySeed);
    const rawTransactionCountData = generateTransactionCountData(companySeed, startDate, endDate, granularity);
    const rawPreviousTransactionCountData = generatePreviousTransactionCountData(rawTransactionCountData, companySeed);
    const rawAverageTransactionData = generateAverageTransactionData(companySeed, startDate, endDate, granularity);
    const rawPreviousAverageTransactionData = generatePreviousAverageTransactionData(rawAverageTransactionData, companySeed);
    const rawPaymentMethodsData = generatePaymentMethodsData();
    const rawGeoPaymentData = generateGeoPaymentData();
    const rawRefundData = generateRefundData(companySeed, startDate, endDate, granularity);
    const rawDisputeActivityData = generateDisputeActivityData(companySeed, startDate, endDate, granularity);
    const rawRefundRateData = generateRefundRateData(companySeed, startDate, endDate, granularity);
    const rawPreviousRefundRateData = generatePreviousRefundRateData(rawRefundRateData, companySeed);
    const rawNewCustomersData = generateNewCustomersData(companySeed, startDate, endDate, granularity);
    const rawPreviousNewCustomersData = generatePreviousNewCustomersData(rawNewCustomersData, companySeed);
    const rawDisputeCountData = generateDisputeCountData(companySeed, startDate, endDate, granularity);
    const rawPreviousDisputeCountData = generatePreviousDisputeCountData(rawDisputeCountData, companySeed);
    const rawBlockedPaymentsData = generateBlockedPaymentsData(companySeed, startDate, endDate, granularity);
    const rawPreviousBlockedPaymentsData = generatePreviousBlockedPaymentsData(rawBlockedPaymentsData, companySeed);
    const rawBlockedPaymentsVolumeData = generateBlockedPaymentsVolumeData(companySeed, startDate, endDate, granularity);
    const rawPreviousBlockedPaymentsVolumeData = generatePreviousBlockedPaymentsVolumeData(rawBlockedPaymentsVolumeData, companySeed);
    const selectedCompanyIds = selectedCompanies.map(c => c.id);
    const isAllCompanies = selectedCompanyIds.length === companies.length || selectedCompanyIds.length === 0;
    if (isAllCompanies) {
      setPaymentVolumeData(rawPaymentVolumeData);
      setPreviousPaymentVolumeData(rawPreviousPaymentVolumeData);
      setTransactionCountData(rawTransactionCountData);
      setPreviousTransactionCountData(rawPreviousTransactionCountData);
      setAverageTransactionData(rawAverageTransactionData);
      setPreviousAverageTransactionData(rawPreviousAverageTransactionData);
      setPaymentMethodsData(rawPaymentMethodsData);
      setGeoPaymentData(rawGeoPaymentData);
      setRefundData(rawRefundData);
      setDisputeActivityData(rawDisputeActivityData);
      setRefundRateData(rawRefundRateData);
      setPreviousRefundRateData(rawPreviousRefundRateData);
      setNewCustomersData(rawNewCustomersData);
      setPreviousNewCustomersData(rawPreviousNewCustomersData);
      setDisputeCountData(rawDisputeCountData);
      setPreviousDisputeCountData(rawPreviousDisputeCountData);
      setBlockedPaymentsData(rawBlockedPaymentsData);
      setPreviousBlockedPaymentsData(rawPreviousBlockedPaymentsData);
      setBlockedPaymentsVolumeData(rawBlockedPaymentsVolumeData);
      setPreviousBlockedPaymentsVolumeData(rawPreviousBlockedPaymentsVolumeData);
    } else {
      setPaymentVolumeData(filterDataByCompanies(rawPaymentVolumeData, selectedCompanyIds));
      setPreviousPaymentVolumeData(filterDataByCompanies(rawPreviousPaymentVolumeData, selectedCompanyIds));
      setTransactionCountData(filterDataByCompanies(rawTransactionCountData, selectedCompanyIds));
      setPreviousTransactionCountData(filterDataByCompanies(rawPreviousTransactionCountData, selectedCompanyIds));
      setAverageTransactionData(filterDataByCompanies(rawAverageTransactionData, selectedCompanyIds));
      setPreviousAverageTransactionData(filterDataByCompanies(rawPreviousAverageTransactionData, selectedCompanyIds));
      setPaymentMethodsData(rawPaymentMethodsData);
      setGeoPaymentData(filterDataByCompanies(rawGeoPaymentData, selectedCompanyIds));
      setRefundData(filterDataByCompanies(rawRefundData, selectedCompanyIds));
      setDisputeActivityData(filterDataByCompanies(rawDisputeActivityData, selectedCompanyIds));
      setRefundRateData(filterDataByCompanies(rawRefundRateData, selectedCompanyIds));
      setPreviousRefundRateData(filterDataByCompanies(rawPreviousRefundRateData, selectedCompanyIds));
      setNewCustomersData(filterDataByCompanies(rawNewCustomersData, selectedCompanyIds));
      setPreviousNewCustomersData(filterDataByCompanies(rawPreviousNewCustomersData, selectedCompanyIds));
      setDisputeCountData(filterDataByCompanies(rawDisputeCountData, selectedCompanyIds));
      setPreviousDisputeCountData(filterDataByCompanies(rawPreviousDisputeCountData, selectedCompanyIds));
      setBlockedPaymentsData(filterDataByCompanies(rawBlockedPaymentsData, selectedCompanyIds));
      setPreviousBlockedPaymentsData(filterDataByCompanies(rawPreviousBlockedPaymentsData, selectedCompanyIds));
      setBlockedPaymentsVolumeData(filterDataByCompanies(rawBlockedPaymentsVolumeData, selectedCompanyIds));
      setPreviousBlockedPaymentsVolumeData(filterDataByCompanies(rawPreviousBlockedPaymentsVolumeData, selectedCompanyIds));
    }
  }, [selectedCompanies, companies, dateRange, granularity, comparison, paymentMethods, selectedCompany]);
  // --- Metrics Calculations ---
  const totalVolume = paymentVolumeData.reduce((sum, item) => sum + item.amount, 0);
  const totalTransactions = transactionCountData.reduce((sum, item) => sum + item.count, 0);
  const totalRefundAmount = refundData.reduce((sum, item) => sum + item.amount, 0);
  const refundPercentage = totalVolume > 0 ? totalRefundAmount / totalVolume * 100 : 0;
  const disputeRate = disputeActivityData.length > 0 ? disputeActivityData[disputeActivityData.length - 1].rate : 0;
  const netVolume = totalVolume * 0.979; // net after fees
  const paymentSuccessRate = totalTransactions > 0 ? 72.0 : 0;
  const totalDisputeCount = disputeCountData.reduce((sum, item) => sum + item.count, 0);
  const totalBlockedCount = blockedPaymentsData.reduce((sum, item) => sum + item.count, 0);
  const totalBlockedVolume = blockedPaymentsVolumeData.reduce((sum, item) => sum + item.amount, 0);
  const spendPerCustomer = totalVolume > 0 ? totalVolume / 287 : 0;
  const calculateGrowth = (current: number, previous: number) => {
    if (!previous || previous === 0) return 0;
    return ((current - previous) / previous * 100).toFixed(2);
  };
  const formatCurrencyCompact = (value: number) => {
    if (value >= 1000000) return `${getTenant().currency.code} ${(value / 1000000).toFixed(2)}Mn`;
    if (value >= 1000) return `${getTenant().currency.code} ${(value / 1000).toFixed(2)}K`;
    return formatCurrencyAmount(value, {
      fractionDigits: 2
    });
  };
  const formatYAxis = (value: number) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(0)}Mn`;
    if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
    if (value > 0 && value < 1) return value.toFixed(1);
    return value.toString();
  };
  const formatDate = (date: string, gran: string) => {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    if (gran === 'hourly') return d.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    if (gran === 'quarterly') return `Q${Math.floor(d.getMonth() / 3) + 1} ${d.getFullYear()}`;
    if (gran === 'yearly') return d.getFullYear().toString();
    if (gran === 'monthly') return d.toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric'
    });
    if (gran === 'weekly') return `W${Math.ceil(d.getDate() / 7)} ${d.toLocaleDateString('en-US', {
      month: 'short'
    })}`;
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };
  const getUpdateTime = () => {
    const minutes = Math.floor(Math.random() * 10) + 1;
    return `Updated ${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  };
  // --- Actions & Alerts Data ---
  const pendingBills = mockBills.filter(b => b.status === 'pending_approval');
  const upcomingBills = [...mockBills].filter(b => b.status === 'approved' || b.status === 'pending_approval').sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()).slice(0, 5);
  const recentActivity = [{
    id: 1,
    type: 'payment',
    title: 'Payment received',
    desc: `${getTenant().currency.code} 45,000 from John Doe`,
    time: '10 mins ago',
    icon: DollarSignIcon,
    color: 'text-gray-600 bg-gray-100'
  }, {
    id: 2,
    type: 'bill',
    title: 'Bill approved',
    desc: 'INV-2026-003 by Simon Verraest',
    time: '1 hour ago',
    icon: CheckCircleIcon,
    color: 'text-gray-600 bg-gray-100'
  }, {
    id: 3,
    type: 'dispute',
    title: 'New dispute opened',
    desc: `${getTenant().currency.code} 1,538.94 - Fraudulent`,
    time: '3 hours ago',
    icon: AlertTriangleIcon,
    color: 'text-gray-600 bg-gray-100'
  }, {
    id: 4,
    type: 'refund',
    title: 'Refund processed',
    desc: `${getTenant().currency.code} 2,500 to Sarah Smith`,
    time: '5 hours ago',
    icon: RotateCcwIcon,
    color: 'text-gray-600 bg-gray-100'
  }, {
    id: 5,
    type: 'payment',
    title: 'Payment received',
    desc: `${getTenant().currency.code} 12,400 from Acme Corp`,
    time: 'Yesterday',
    icon: DollarSignIcon,
    color: 'text-gray-600 bg-gray-100'
  }];
  // --- Render Chart Helper ---
  const renderChart = (chartId: string) => {
    switch (chartId) {
      case 'gross-volume':
        return <MetricChart key={chartId} title={t('pages.Nadi.gross_volume_2')} tooltipText="Estimated amount for captured payments, shown in your default settlement currency using average exchange rates over the selected time period. Gross sales figures include payments that were later disputed or refunded." currentValue={formatCurrencyCompact(totalVolume)} growthPercent={comparison !== 'none' ? parseFloat(calculateGrowth(totalVolume, totalVolume * 0.92)) : undefined} previousValue={comparison !== 'none' ? formatCurrencyCompact(totalVolume * 0.92) : undefined} data={paymentVolumeData} previousData={comparison !== 'none' ? previousPaymentVolumeData : []} dataKey="amount" yAxisFormatter={formatYAxis} tooltipValueFormatter={value => formatCurrencyAmount(value, {
          fractionDigits: 0
        })} dateFormatter={date => formatDate(date, granularity)} updateTime={getUpdateTime()} data-id="element-2461" />;
      case 'successful-payments':
        return <MetricChart key={chartId} title={t('pages.renderChart.attrs.successful_payments')} tooltipText="The number of successful payments over the given time period." currentValue={totalTransactions.toLocaleString()} growthPercent={comparison !== 'none' ? parseFloat(calculateGrowth(totalTransactions, totalTransactions * 0.94)) : undefined} previousValue={comparison !== 'none' ? (totalTransactions * 0.94).toLocaleString() : undefined} data={transactionCountData} previousData={comparison !== 'none' ? previousTransactionCountData : []} dataKey="count" tooltipValueFormatter={value => `${value.toLocaleString()}`} tooltipLabel="payments" dateFormatter={date => formatDate(date, granularity)} updateTime={getUpdateTime()} data-id="element-2462" />;
      case 'refund-rate':
        return <MetricChart key={chartId} title={t('components.RefundsTab.refund_rate')} tooltipText="Refund rate represents the total number of payments that have partial and full refunds divided by the total number of successful payments in the same period." currentValue={`${refundPercentage.toFixed(1)}%`} growthPercent={comparison !== 'none' ? parseFloat(calculateGrowth(refundPercentage, refundPercentage * 1.05)) : undefined} previousValue={comparison !== 'none' ? `${(refundPercentage * 1.05).toFixed(1)}%` : undefined} data={refundRateData} previousData={comparison !== 'none' ? previousRefundRateData : []} dataKey="rate" yAxisFormatter={v => `${v}%`} tooltipValueFormatter={value => `${value}%`} dateFormatter={date => formatDate(date, granularity)} updateTime={getUpdateTime()} data-id="element-2463" />;
      case 'payment-methods':
        return <div key={chartId} className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm" data-id="element-2464">
            <div className="flex items-center mb-6" data-id="element-2465">
              <h3 className="text-sm font-semibold text-gray-900" data-id="element-2466">
                {t('pages.renderChart.top_payment_method_categories')}
              </h3>
              <InfoTooltip content="Estimated gross volume is the total sales volume per payment method. This includes refunds and disputes." data-id="element-2467" />
            </div>
            <div className="mb-6" data-id="element-2468">
              <div className="h-4 flex rounded-full overflow-hidden" data-id="element-2469">
                <div style={{
                width: '45%',
                backgroundColor: '#A78BFA'
              }} data-id="element-2470"></div>
                <div style={{
                width: '34%',
                backgroundColor: '#93C5FD'
              }} data-id="element-2471"></div>
                <div style={{
                width: '12%',
                backgroundColor: '#F472B6'
              }} data-id="element-2472"></div>
                <div style={{
                width: '5%',
                backgroundColor: '#FBBF24'
              }} data-id="element-2473"></div>
                <div style={{
                width: '4%',
                backgroundColor: '#92400E'
              }} data-id="element-2474"></div>
              </div>
            </div>
            <div className="space-y-4" data-id="element-2475">
              <div className="flex justify-between items-center" data-id="element-2476">
                <div className="flex items-center gap-2" data-id="element-2477">
                  <div className="w-3 h-3 rounded-full" style={{
                  backgroundColor: '#A78BFA'
                }} data-id="element-2478"></div>
                  <span className="text-sm text-gray-600" data-id="element-2479">
                    {t('pages.renderChart.card')}
                  </span>
                </div>
                <span className="text-sm font-semibold text-gray-900" data-id="element-2480">
                  {t('pages.renderChart.usd_24090k')}
                </span>
              </div>
              <div className="flex justify-between items-center" data-id="element-2481">
                <div className="flex items-center gap-2" data-id="element-2482">
                  <div className="w-3 h-3 rounded-full" style={{
                  backgroundColor: '#93C5FD'
                }} data-id="element-2483"></div>
                  <span className="text-sm text-gray-600" data-id="element-2484">
                    {t('pages.renderChart.bank_transfer')}
                  </span>
                </div>
                <span className="text-sm font-semibold text-gray-900" data-id="element-2485">
                  {t('pages.renderChart.usd_18050k')}
                </span>
              </div>
              <div className="flex justify-between items-center" data-id="element-2486">
                <div className="flex items-center gap-2" data-id="element-2487">
                  <div className="w-3 h-3 rounded-full" style={{
                  backgroundColor: '#F472B6'
                }} data-id="element-2488"></div>
                  <span className="text-sm text-gray-600" data-id="element-2489">
                    {t('pages.renderChart.ewallet')}
                  </span>
                </div>
                <span className="text-sm font-semibold text-gray-900" data-id="element-2490">
                  {t('pages.renderChart.usd_9520k')}
                </span>
              </div>
              <div className="flex justify-between items-center" data-id="element-2491">
                <div className="flex items-center gap-2" data-id="element-2492">
                  <div className="w-3 h-3 rounded-full" style={{
                  backgroundColor: '#FBBF24'
                }} data-id="element-2493"></div>
                  <span className="text-sm text-gray-600" data-id="element-2494">
                    {t('pages.renderChart.crypto')}
                  </span>
                </div>
                <span className="text-sm font-semibold text-gray-900" data-id="element-2495">
                  {t('pages.renderChart.usd_1230k')}
                </span>
              </div>
              <div className="flex justify-between items-center" data-id="element-2496">
                <div className="flex items-center gap-2" data-id="element-2497">
                  <div className="w-3 h-3 rounded-full" style={{
                  backgroundColor: '#92400E'
                }} data-id="element-2498"></div>
                  <span className="text-sm text-gray-600" data-id="element-2499">
                    {t('pages.renderChart.other')}
                  </span>
                </div>
                <span className="text-sm font-semibold text-gray-900" data-id="element-2500">
                  {t('pages.renderChart.usd_510k')}
                </span>
              </div>
            </div>
            <div className="flex justify-between items-center mt-5 pt-3 border-t border-gray-100" data-id="element-2501">
              <span className="text-xs text-gray-400" data-id="element-2502">{getUpdateTime()}</span>
              <button onClick={() => navigate('/payment-analytics?tab=payment-methods')} className="text-xs text-blue-600 hover:text-blue-700 font-medium" data-id="element-2503">
                {t('components.ListWidget.view_more')}
              </button>
            </div>
          </div>;
      case 'new-customers':
        return <MetricChart key={chartId} title={t('pages.CustomMetrics.new_customers')} tooltipText="Number of new customers created, including customers that are subsequently deleted. This number does not include guest customers." currentValue="287" growthPercent={15.2} previousValue="249" data={newCustomersData} previousData={previousNewCustomersData} dataKey="count" tooltipValueFormatter={value => `${value}`} tooltipLabel="customers" dateFormatter={date => formatDate(date, granularity)} updateTime={getUpdateTime()} data-id="element-2504" />;
      case 'net-volume':
        return <MetricChart key={chartId} title={t('pages.renderChart.attrs.net_volume')} tooltipText="Estimated volume from payments that are settled to your balance after fees, refunds, disputes, and transfers have been deducted." currentValue={formatCurrencyCompact(netVolume)} growthPercent={comparison !== 'none' ? parseFloat(calculateGrowth(netVolume, netVolume * 0.935)) : undefined} previousValue={comparison !== 'none' ? formatCurrencyCompact(netVolume * 0.935) : undefined} data={paymentVolumeData.map(d => ({
          ...d,
          amount: d.amount * 0.979
        }))} previousData={comparison !== 'none' ? previousPaymentVolumeData.map(d => ({
          ...d,
          amount: d.amount * 0.979
        })) : []} dataKey="amount" yAxisFormatter={formatYAxis} tooltipValueFormatter={value => formatCurrencyAmount(value, {
          fractionDigits: 0
        })} dateFormatter={date => formatDate(date, granularity)} updateTime={getUpdateTime()} data-id="element-2505" />;
      case 'payment-success-rate':
        return <MetricChart key={chartId} title={t('components.AcceptanceFilters.payment_success_rate')} tooltipText="The payment success rate is a measure of how many payment attempts are successfully accepted by payment providers, such as card issuers or local payment methods." currentValue={`${paymentSuccessRate.toFixed(1)}%`} growthPercent={comparison !== 'none' ? 6.17 : undefined} previousValue={comparison !== 'none' ? '69.1%' : undefined} data={transactionCountData.map(d => ({
          ...d,
          rate: 65 + Math.random() * 15
        }))} previousData={[]} dataKey="rate" yAxisFormatter={v => `${v}%`} tooltipValueFormatter={value => `${value.toFixed(1)}%`} dateFormatter={date => formatDate(date, granularity)} updateTime={getUpdateTime()} data-id="element-2506" />;
      case 'refunds-volume':
        return <MetricChart key={chartId} title={t('pages.renderChart.attrs.refunds_volume')} tooltipText="Amount of refunds conducted over the selected time period. The total reflects an estimated amount converted into your default settlement currency using average exchange rates over the time period." currentValue={formatCurrencyCompact(totalRefundAmount)} growthPercent={comparison !== 'none' ? -9.09 : undefined} previousValue={comparison !== 'none' ? formatCurrencyCompact(totalRefundAmount * 1.1) : undefined} data={refundData} previousData={[]} dataKey="amount" yAxisFormatter={formatYAxis} tooltipValueFormatter={value => formatCurrencyAmount(value, {
          fractionDigits: 0
        })} dateFormatter={date => formatDate(date, granularity)} updateTime={getUpdateTime()} data-id="element-2507" />;
      case 'total-payments':
        return <ListWidget key={chartId} title={t('pages.renderChart.attrs.total_payments')} tooltipText="Snapshot of the latest status for all payment intents created in the selected time period." items={[{
          label: 'Succeeded',
          value: '48,490'
        }, {
          label: 'Authorized',
          value: '3,109'
        }, {
          label: 'Failed',
          value: '2,160'
        }, {
          label: 'Refunded & Partially refunded',
          value: '11,094'
        }, {
          label: 'Refund failed',
          value: '114'
        }]} updateTime={getUpdateTime()} data-id="element-2508" />;
      case 'dispute-count':
        return <MetricChart key={chartId} title={t('pages.renderChart.attrs.dispute_count')} tooltipText="Estimated number of disputes. RFIs and pre-chargebacks are not included" currentValue={totalDisputeCount.toString()} growthPercent={comparison !== 'none' ? 12.5 : undefined} previousValue={comparison !== 'none' ? '37' : undefined} data={disputeCountData} previousData={comparison !== 'none' ? previousDisputeCountData : []} dataKey="count" tooltipValueFormatter={value => `${value}`} tooltipLabel="disputes" dateFormatter={date => formatDate(date, granularity)} updateTime={getUpdateTime()} data-id="element-2509" />;
      case 'dispute-activity':
        return <MetricChart key={chartId} title={t('pages.renderChart.attrs.dispute_activity')} tooltipText="Estimated number of disputes divided by the number of successful payments in the same time series. RFIs and pre-chargebacks are not included in your dispute rate." currentValue={`${disputeRate.toFixed(1)}%`} growthPercent={comparison !== 'none' ? -8.3 : undefined} previousValue={comparison !== 'none' ? `${(disputeRate * 1.09).toFixed(1)}%` : undefined} data={disputeActivityData} previousData={[]} dataKey="rate" yAxisFormatter={v => `${v}%`} tooltipValueFormatter={value => `${value}%`} dateFormatter={date => formatDate(date, granularity)} updateTime={getUpdateTime()} data-id="element-2510" />;
      case 'disputed-payments':
        return <ListWidget key={chartId} title={t('pages.renderChart.attrs.disputed_payments')} tooltipText="Recent disputed transactions." items={[{
          label: `${getTenant().currency.code} 30.50 · Fraudulent`,
          value: 'Requires response',
          valueColor: 'text-emerald-600'
        }, {
          label: `${getTenant().currency.code} 122.25 · Duplicate`,
          value: 'Challenged',
          valueColor: 'text-blue-600'
        }, {
          label: `${getTenant().currency.code} 16.00 · Fraudulent`,
          value: 'Lost',
          valueColor: 'text-red-600'
        }]} updateTime={getUpdateTime()} data-id="element-2511" />;
      case 'failed-payments':
        return <ListWidget key={chartId} title={t('pages.renderChart.attrs.failed_payments')} tooltipText="Most recent payment failures excluding blocked payments." items={[{
          label: `${getTenant().currency.code} 46.00 · Nov 1, 2:15 PM`,
          value: 'Failed',
          valueColor: 'text-red-600'
        }, {
          label: `${getTenant().currency.code} 24.60 · Aug 3, 2:38 AM`,
          value: 'Failed',
          valueColor: 'text-red-600'
        }]} updateTime={getUpdateTime()} data-id="element-2512" />;
      case 'spend-per-customer':
        return <MetricChart key={chartId} title={t('pages.renderChart.attrs.spend_per_customer')} tooltipText="Estimated gross volume for all payments created with a customer, divided by the total number of customers with payments." currentValue={formatCurrencyCompact(spendPerCustomer)} growthPercent={comparison !== 'none' ? 5.8 : undefined} previousValue={comparison !== 'none' ? formatCurrencyCompact(spendPerCustomer * 0.945) : undefined} data={averageTransactionData} previousData={comparison !== 'none' ? previousAverageTransactionData : []} dataKey="amount" yAxisFormatter={formatYAxis} tooltipValueFormatter={value => formatCurrencyAmount(value, {
          fractionDigits: 0
        })} dateFormatter={date => formatDate(date, granularity)} updateTime={getUpdateTime()} data-id="element-2513" />;
      case 'top-customers':
        return <ListWidget key={chartId} title={t('pages.renderChart.attrs.top_customers_by_spend')} tooltipText="Estimated gross volume is the total spend per customer, including guest customers. The numbers do not include refunds and disputes." items={[{
          label: 'Ahmad Razif',
          value: `${getTenant().currency.code} 4,523`
        }, {
          label: 'Nurul Ain Binti Hassan',
          value: `${getTenant().currency.code} 3,845`
        }, {
          label: 'Mohd Faizal Ibrahim',
          value: `${getTenant().currency.code} 2,987`
        }, {
          label: 'Siti Norzahirah',
          value: `${getTenant().currency.code} 2,234`
        }, {
          label: 'Lim Wei Jian',
          value: `${getTenant().currency.code} 1,892`
        }]} updateTime={getUpdateTime()} data-id="element-2514" />;
      case 'top-countries':
        return <ListWidget key={chartId} title={t('pages.renderChart.attrs.top_countries_by_spend')} tooltipText="Estimated gross volume by country of the payment method used. The numbers include refunds and disputes." items={[{
          label: '🇲🇾 Malaysia',
          value: `${getTenant().currency.code} 185,640`
        }, {
          label: '🇸🇬 Singapore',
          value: `${getTenant().currency.code} 112,350`
        }, {
          label: '🇺🇸 United States',
          value: `${getTenant().currency.code} 87,420`
        }, {
          label: '🇬🇧 United Kingdom',
          value: `${getTenant().currency.code} 64,280`
        }, {
          label: '🌐 Other',
          value: `${getTenant().currency.code} 106,330`
        }]} updateTime={getUpdateTime()} onViewMore={() => navigate('/payment-analytics?tab=payment-methods')} data-id="element-2515" />;
      case 'blocked-payments':
        return <MetricChart key={chartId} title={t('pages.renderChart.attrs.blocked_payments')} tooltipText="Count of payments blocked due to risk settings" currentValue={totalBlockedCount.toString()} growthPercent={comparison !== 'none' ? 22.5 : undefined} previousValue={comparison !== 'none' ? '55' : undefined} data={blockedPaymentsData} previousData={comparison !== 'none' ? previousBlockedPaymentsData : []} dataKey="count" tooltipValueFormatter={value => `${value}`} tooltipLabel="blocked" dateFormatter={date => formatDate(date, granularity)} updateTime={getUpdateTime()} data-id="element-2516" />;
      case 'blocked-payments-volume':
        return <MetricChart key={chartId} title={t('pages.renderChart.attrs.blocked_payments_volume')} tooltipText="Volume of payments blocked due to risk settings" currentValue={formatCurrencyCompact(totalBlockedVolume)} growthPercent={comparison !== 'none' ? 18.2 : undefined} previousValue={comparison !== 'none' ? formatCurrencyCompact(totalBlockedVolume * 0.846) : undefined} data={blockedPaymentsVolumeData} previousData={comparison !== 'none' ? previousBlockedPaymentsVolumeData : []} dataKey="amount" yAxisFormatter={formatYAxis} tooltipValueFormatter={value => formatCurrencyAmount(value, {
          fractionDigits: 0
        })} dateFormatter={date => formatDate(date, granularity)} updateTime={getUpdateTime()} data-id="element-2517" />;
      default:
        return null;
    }
  };
  return <div data-id="element-2518">
      {/* ================= CURRENT SNAPSHOT ================= */}
      <div className="mb-5" data-id="element-2519">
        <h2 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-2520">
          {t('pages.Overview.current_snapshot')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-2521">
          <BalanceSnapshotWidget data-id="element-2522" />
          <AttentionNeededWidget data-id="element-2523" />
        </div>
      </div>

      {/* ================= OVERVIEW (ANALYTICS) ================= */}
      <div className="space-y-4" data-id="element-2524">
        {/* Section Header */}
        <h2 className="text-xl font-semibold text-gray-900" data-id="element-2525">
          {t('pages.Overview.overview')}
        </h2>

        {/* Filters Bar + Add/Edit */}
        <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-2526">
          <div className="flex flex-wrap items-center gap-2" data-id="element-2527">
            <DateRangePicker value={dateRange} onChange={handleDateRangeChange} data-id="element-2528" />

            <GranularitySelector value={granularity} onChange={handleGranularityChange} allowedGranularities={availableGranularities} hidden={isGranularityHidden} data-id="element-2529" />

            <ComparisonSelector value={comparison} onChange={setComparison} customRange={customComparisonRange} onCustomRangeChange={setCustomComparisonRange} data-id="element-2530" />

            <PaymentMethodFilter value={paymentMethods} onChange={setPaymentMethods} data-id="element-2531" />
          </div>
          <div className="flex items-center gap-2" data-id="element-2532">
            <button onClick={() => setShowAddChartModal(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors" data-id="element-2533">
              <PlusIcon data-id="element-2534" /> {t('pages.Overview.add')}
            </button>
            <button onClick={() => setShowEditChartModal(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors" data-id="element-2535">
              <EditIcon data-id="element-2536" /> {t('pages.Nadi.edit')}
            </button>
          </div>
        </div>

        {/* Analytics Chart Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-2537">
          {chartOrder.filter(id => visibleCharts.has(id)).map(chartId => renderChart(chartId))}
        </div>
      </div>

      {/* Modals */}
      {showAddChartModal && <AddChartModal onClose={() => setShowAddChartModal(false)} onAdd={ids => setVisibleCharts(new Set(ids))} initialSelectedCharts={visibleCharts} renderChart={renderChart} data-id="element-2538" />}
      {showEditChartModal && <EditChartsModal onClose={() => setShowEditChartModal(false)} onSave={(order, visible) => {
      setChartOrder(order);
      setVisibleCharts(visible);
    }} initialChartOrder={chartOrder} initialVisibleCharts={visibleCharts} chartData={new Map()} renderChart={renderChart} data-id="element-2539" />}
    </div>;
}
// Helper icons for buttons
function PlusIcon(props: any) {
  return <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" {...props} data-id="element-2540">
      <path d="M8 2a.75.75 0 01.75.75v4.5h4.5a.75.75 0 010 1.5h-4.5v4.5a.75.75 0 01-1.5 0v-4.5h-4.5a.75.75 0 010-1.5h4.5v-4.5A.75.75 0 018 2z" data-id="element-2541" />
    </svg>;
}
function EditIcon(props: any) {
  return <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" {...props} data-id="element-2542">
      <path d="M11.013 2.513a1.75 1.75 0 012.475 2.474l-8.61 8.61a.75.75 0 01-.353.194l-3.75.75a.75.75 0 01-.877-.877l.75-3.75a.75.75 0 01.194-.353l8.61-8.61z" data-id="element-2543" />
    </svg>;
}